import { useSearchParams } from 'react-router-dom';
import {
  LearningShell,
  MetricStrip,
  PageIntro,
  Panel,
} from '../features/learning-space/components';
import { useLearningViewer } from '../features/learning-space/useLearningViewer';
import { buildLearningRouteScope } from '@examflow/learning-space';
import { buildRetentionToolBackAction } from '../features/learning-space/retentionToolsModel';
import { AchievementBadge } from '../components/achievements/AchievementBadge';
import {
  getAchievements,
  getStreakData,
  getEarnedCount,
  getTotalCount,
  getCumulativeStats,
} from '../data/local/achievements';
import type { MetricModel, UiTone } from '@examflow/learning-space';

export default function AchievementsPage() {
  const [searchParams] = useSearchParams();
  const routeScope = buildLearningRouteScope(searchParams);
  const viewer = useLearningViewer(routeScope);

  const achievements = getAchievements();
  const streakData = getStreakData();
  const earnedCount = getEarnedCount();
  const totalCount = getTotalCount();
  const cumulative = getCumulativeStats();
  const earnedAchievements = achievements.filter((a) => a.earned);
  const lockedAchievements = achievements.filter((a) => !a.earned);
  const progressPct = totalCount > 0 ? Math.round((earnedCount / totalCount) * 100) : 0;

  const metrics: MetricModel[] = [
    {
      label: '已解锁',
      value: String(earnedCount),
      tone: 'success' as UiTone,
      caption: `共 ${totalCount} 个`,
    },
    {
      label: '完成进度',
      value: `${progressPct}%`,
      tone: 'current' as UiTone,
      caption: '本机徽章',
    },
    {
      label: '当前连胜',
      value: String(streakData.current),
      tone: (streakData.current >= 3 ? 'success' : 'warning') as UiTone,
      caption: '天',
    },
    {
      label: '历史最佳',
      value: String(streakData.best),
      tone: 'neutral' as UiTone,
      caption: `累计练习 ${cumulative.practiceCount} 次`,
    },
  ];

  return (
    <LearningShell viewer={viewer} activeNav="workspace" statusLabel="留存工具">
      <PageIntro
        eyebrow="留存工具 · 非主闭环"
        title="成就"
        subtitle="徽章与里程碑保存在本设备，仅作留存激励，不进入主训练闭环，也不写入能力诊断。"
        secondaryAction={buildRetentionToolBackAction(routeScope)}
      />

      <MetricStrip metrics={metrics} />

      <div className="mt-7 space-y-6">
        <Panel title="总体进度" subtitle="解锁进度仅反映本机练习足迹">
          <div className="w-full bg-slate-100 rounded-full h-3">
            <div
              className="bg-[#b86b16] h-3 rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="text-sm text-slate-500 mt-3">
            已解锁 {earnedCount} 个成就，还有 {Math.max(totalCount - earnedCount, 0)} 个待解锁
          </p>
        </Panel>

        {earnedAchievements.length > 0 ? (
          <Panel title="已解锁成就" subtitle="本机已达成的里程碑">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {earnedAchievements.map((achievement) => (
                <AchievementBadge
                  key={achievement.id}
                  achievement={achievement}
                  size="lg"
                  showDetails
                />
              ))}
            </div>
          </Panel>
        ) : null}

        {lockedAchievements.length > 0 ? (
          <Panel title="待解锁成就" subtitle="完成对应练习足迹后自动解锁">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {lockedAchievements.map((achievement) => (
                <AchievementBadge
                  key={achievement.id}
                  achievement={achievement}
                  size="lg"
                  showDetails
                />
              ))}
            </div>
          </Panel>
        ) : null}

        {achievements.length === 0 ? (
          <Panel title="成就" subtitle="本机暂无成就定义">
            <p className="text-sm text-slate-500">暂无成就数据</p>
          </Panel>
        ) : null}
      </div>
    </LearningShell>
  );
}
